// Typed wrappers around `/api/staff/users/*` (feature F-A).
//
// Mirrors shared/src/api/zones.ts. Consumed by staff/src/features/users/.
// All endpoints are admin-only (enforced server-side via ROLE_PERMS); a
// non-admin caller gets an ApiError with status 403.

import { getApiClient } from "./client";
import type { components } from "./types";

export type User = components["schemas"]["UserResponse"];
export type UserCreate = components["schemas"]["UserCreate"];
export type UserUpdate = components["schemas"]["UserUpdate"];
export type UserPasswordReset = components["schemas"]["UserPasswordReset"];
export type MessageResponse = components["schemas"]["MessageResponse"];

export async function listUsers(opts?: {
  includeDeleted?: boolean;
  enabled?: boolean;
}): Promise<User[]> {
  const params: Record<string, string | boolean> = {};
  if (opts?.includeDeleted) params.include_deleted = true;
  if (opts?.enabled !== undefined) params.enabled = opts.enabled;
  const { data } = await getApiClient().get<User[]>("/staff/users", { params });
  return data;
}

export async function getUser(userId: string): Promise<User> {
  const { data } = await getApiClient().get<User>(`/staff/users/${userId}`);
  return data;
}

export async function createUser(payload: UserCreate): Promise<User> {
  const { data } = await getApiClient().post<User>("/staff/users", payload);
  return data;
}

export async function updateUser(
  userId: string,
  payload: UserUpdate,
): Promise<User> {
  const { data } = await getApiClient().patch<User>(
    `/staff/users/${userId}`,
    payload,
  );
  return data;
}

export async function enableUser(userId: string): Promise<User> {
  const { data } = await getApiClient().post<User>(
    `/staff/users/${userId}/enable`,
  );
  return data;
}

export async function disableUser(userId: string): Promise<User> {
  const { data } = await getApiClient().post<User>(
    `/staff/users/${userId}/disable`,
  );
  return data;
}

export async function deleteUser(userId: string): Promise<MessageResponse> {
  const { data } = await getApiClient().delete<MessageResponse>(
    `/staff/users/${userId}`,
  );
  return data;
}

export async function resetUserPassword(
  userId: string,
  password: string,
): Promise<MessageResponse> {
  const { data } = await getApiClient().post<MessageResponse>(
    `/staff/users/${userId}/password`,
    { password },
  );
  return data;
}
